


import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdmins, deleteAdmin } from "../redux/actions/AdminParkActions";

import theme from "../components/shared/theme";
import { thStyle, tdStyle } from "../components/shared/themeStyles";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const ITEMS_PER_PAGE = 10;

const dropdownStyle = {
  padding: theme.spacing.sm,
  border: "1px solid #ccc",
  borderRadius: theme.radius.md,
};

// CSV helper
const convertToCSV = (data) => {
  const headers = ["Username", "Email", "Phone", "District", "Municipality", "Created At"];

  const rows = data.map((a) => [
    a.username,
    a.email,
    a.phone,
    a.district_name,
    a.municipality_name,
    new Date(a.created_at).toLocaleDateString("en-IN"),
  ]);

  return [headers, ...rows].map((row) => row.join(",")).join("\n");
};

const ViewParkAdminUser = () => {
  const dispatch = useDispatch();
  const { admins = [], loading, error } = useSelector((state) => state.adminPark || {});

  const { token } = useSelector((state) => state.auth) || {};

  // Delete modal
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Filters
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchAdmins(token));
  }, [dispatch, token]);

  const resetDates = () => {
    setFromDate("");
    setToDate("");
  };

  const handleDownloadCSV = () => {
    const blob = new Blob([convertToCSV(filteredAdmins)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "admin_list.csv";
    a.click();

    URL.revokeObjectURL(url);
  };

  // Filter lists
  const districts = [...new Set(admins.map((a) => a.district_name).filter(Boolean))];
  const municipalities = [...new Set(admins.map((a) => a.municipality_name).filter(Boolean))];

  // Filtering logic
  const filteredAdmins = admins.filter((a) => {
    const matchDistrict = selectedDistrict ? a.district_name === selectedDistrict : true;
    const matchMunicipality = selectedMunicipality ? a.municipality_name === selectedMunicipality : true;

    const matchSearch = searchTerm
      ? [a.username, a.email, a.phone, a.district_name, a.municipality_name]
          .join(" ")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      : true;

    const createdAt = new Date(a.created_at);
    const matchFromDate = fromDate ? createdAt >= new Date(fromDate) : true;
    const matchToDate = toDate ? createdAt <= new Date(toDate) : true;

    return matchDistrict && matchMunicipality && matchSearch && matchFromDate && matchToDate;
  });

  const totalPages = Math.ceil(filteredAdmins.length / ITEMS_PER_PAGE);
  const paginatedAdmins = filteredAdmins.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleDelete = () => {
    dispatch(
      deleteAdmin(deleteId, () => {
        setShowDeleteModal(false);
        setDeleteId(null);
      })
    );
  };

  return (
    <div style={{ backgroundColor: theme.colors.background, padding: theme.spacing.md, minHeight: "100vh" }}>
      <h1 style={{ fontSize: theme.font.size["2xl"], fontWeight: theme.font.weight.bold, marginBottom: theme.spacing.md }}>
        Park Admin List
      </h1>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: theme.spacing.sm,
          marginBottom: theme.spacing.lg,
          backgroundColor: "#fff",
          padding: theme.spacing.md,
          borderRadius: theme.radius.xl,
          boxShadow: theme.shadow.soft,
        }}
      >
        <select
          value={selectedDistrict}
          onChange={(e) => {
            setSelectedDistrict(e.target.value);
            setCurrentPage(1);
          }}
          style={dropdownStyle}
        >
          <option value="">All Districts</option>
          {districts.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        <select
          value={selectedMunicipality}
          onChange={(e) => {
            setSelectedMunicipality(e.target.value);
            setCurrentPage(1);
          }}
          style={dropdownStyle}
        >
          <option value="">All Municipalities</option>
          {municipalities.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{ padding: theme.spacing.sm, border: "1px solid #ccc", borderRadius: theme.radius.md, minWidth: "250px" }}
        />

        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={dropdownStyle} />

        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={dropdownStyle} />

        <button
          onClick={resetDates}
          style={{
            backgroundColor: theme.colors.primary,
            color: "#fff",
            padding: theme.spacing.sm,
            borderRadius: theme.radius.md,
          }}
        >
          Reset Date
        </button>

        <button
          onClick={handleDownloadCSV}
          style={{
            backgroundColor: theme.colors.secondary,
            color: "#fff",
            padding: theme.spacing.sm,
            borderRadius: theme.radius.md,
          }}
        >
          Export CSV
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p style={{ fontSize: theme.font.size.base }}>Loading admins...</p>
      ) : error ? (
        <div
          style={{
            backgroundColor: theme.colors.dangerLight,
            border: `1px solid ${theme.colors.danger}`,
            color: theme.colors.dangerText,
            padding: theme.spacing.sm,
            borderRadius: theme.radius.md,
          }}
        >
          Error: {error}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="min-w-[800px] w-full text-sm text-left">
              <thead style={{ backgroundColor: theme.colors.grayDark, color: "white" }}>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Username</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Phone</th>
                  <th style={thStyle}>District</th>
                  <th style={thStyle}>Municipality</th>
                  <th style={thStyle}>Created At</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {paginatedAdmins.map((admin, index) => (
                  <tr key={admin.id || index} className="hover:bg-gray-50">
                    <td style={tdStyle}>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                    <td style={tdStyle}>{admin.username}</td>
                    <td style={tdStyle}>{admin.email}</td>
                    <td style={tdStyle}>{admin.phone}</td>
                    <td style={tdStyle}>{admin.district_name || "-"}</td>
                    <td style={tdStyle}>{admin.municipality_name || "-"}</td>
                    <td style={tdStyle}>{new Date(admin.created_at).toLocaleDateString("en-IN")}</td>

                    <td style={tdStyle}>
                      <button
                        className="px-2 py-1 text-sm bg-red-600 text-white rounded"
                        onClick={() => {
                          setDeleteId(admin.id);
                          setShowDeleteModal(true);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: theme.spacing.md }}>
            <button
              onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "8px 12px",
                backgroundColor: "#ccc",
                borderRadius: theme.radius.md,
                opacity: currentPage === 1 ? 0.5 : 1,
              }}
            >
              <FiChevronLeft /> Previous
            </button>

            <span style={{ fontSize: theme.font.size.sm }}>
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "8px 12px",
                backgroundColor: "#ccc",
                borderRadius: theme.radius.md,
                opacity: currentPage === totalPages ? 0.5 : 1,
              }}
            >
              Next <FiChevronRight />
            </button>
          </div>
        </>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ background: "#fff", padding: "20px", borderRadius: "10px", width: "350px" }}>
            <h3 style={{ marginBottom: "15px", fontSize: "18px" }}>
              Are you sure you want to delete this admin?
            </h3>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <button
                style={{ background: "red", color: "white", padding: "10px", borderRadius: "8px" }}
                onClick={handleDelete}
              >
                Yes, Delete
              </button>

              <button
                style={{
                  background: "#ddd",
                  padding: "10px",
                  borderRadius: "8px",
                }}
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewParkAdminUser;
